import type {
  AccountHolder,
  CreateUsPersonAccountHolderMutation,
  CreateUsPersonAccountHolderInput,
  UserError,
  AccessDeniedError,
} from "@highnoteplatform/internal-sdk";

import type { HighnoteEnvironment } from ".";

export type {
  HighnoteEnvironment,
  AccountHolder,
  CreateUsPersonAccountHolderInput,
};

export type CreateUsPersonAccountHolderPayload =
  CreateUsPersonAccountHolderMutation["createUSPersonAccountHolder"];

export interface RiskApplicationInternalError extends Error {
  name: "RiskApplicationInternalError";
}

export interface USBusinessAccountHolderFormNotSupported extends Error {
  name: "USBusinessAccountHolderFormNotSupported";
  message: string;
}

export interface AccountHolderFormMissingRequiredField extends Error {
  name: "AccountHolderFormMissingRequiredField";
  message: string;
}

export type RiskApplicationError = Readonly<
  | AccessDeniedError
  | AccountHolderFormMissingRequiredField
  | RiskApplicationInternalError
  | USBusinessAccountHolderFormNotSupported
  | UserError
>;

export type RiskApplicationSdkResponse = Readonly<AccountHolder>;

export interface RiskApplicationSdkConfig {
  /**
   * The form element that will be used to submit the account holder information.
   * The form must have input fields with the following [autocomplete HTTP attribute values](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete#values):
   * - given-name
   * - family-name
   * - address-line1
   * - address-line2
   * - address-level1
   * - address-level2
   * - postal-code
   * - country
   * - bday
   * - tel
   * - email
   */
  form: HTMLFormElement;

  /** A client token generated via the generateRiskApplicationSDKSessionToken mutation */
  riskApplicationSdkToken: string;

  /** The environment to use for making requests to the Highnote API.
   *
   * default value: 'test'
   */
  environment?: HighnoteEnvironment;

  /** A callback to be invoked when an error occurs */
  onError: (error: RiskApplicationError) => void;

  onSuccess: (response: RiskApplicationSdkResponse) => void;
}

export interface RiskApplicationSdkReturn {
  clientToken: string;
  // An option function to be called when the form is being submitted. This function
  // will call Highnote's graphql apis to create the account holder.
  submit: () => Promise<void>;
}
